import React, { useState, useEffect } from "react";
import { Badge, Container, Row, Col, Alert, OverlayTrigger, Tooltip, Form, Button } from "react-bootstrap";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from '../../../services/axios';
import HelperMetiers360 from "../../../services/HelpersMetiers360";
import Loader from "../../../components/widgets/Loader";
import DynamicTable from "../../../components/widgets/dynamicTable/DynamicTable";
import { useStoreState } from "easy-peasy";
import useSearchWithParams from "../../../hooks/useSearchWithParams";
import './PipedrivePage.scss';

const STATUS_VARIANTS = {
    'A traiter': 'primary',
    'Mis en attente': 'warning',
    'Problème de traitement': 'danger',
    'Ignoré': 'secondary',
    'A synchroniser': 'info',
    'Traité': 'success',
}; // or fetch the const from backend

const toDate = (timestamp) => {
    if (!timestamp) return null;
    return typeof timestamp === 'number'
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
};

const formatDate = (timestamp) => {
    const date = toDate(timestamp);
    if (!date) return '-';
    return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PipedrivePage = () => {
    const [messages, setMessages] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [searchParam, setSearchParam] = useSearchParams();
    const [filterType, setFilterType] = useState(() => searchParam.get('type') || '');
    const [filterStatus, setFilterStatus] = useState(() => searchParam.get('status') || '');
    const [showIgnored, setShowIgnored] = useState(() => searchParam.get('showIgnored') === 'true');
    const [showTraite, setShowTraite] = useState(() => searchParam.get('showTraite') === 'true');
    const [isFiltersOpen, setIsFiltersOpen] = useState(() => !!(searchParam.get('type')
        || searchParam.get('status') || searchParam.get('showIgnored') === 'true' || searchParam.get('showTraite') === 'true'));
    const { apiData } = useStoreState(state => state.actionSlugs);
    const pipedriveSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('pipedriveMessage'));
    const navigate = useNavigate();
    const location = useLocation();
    const { searchInput, search, resetSearchBar } = useSearchWithParams({
        placeholder: 'Rechercher par mots-clés (titre, client, note)', delay: 400, width: '100%'
    });

    useEffect(() => {
        const getUrl = apiData.ACTIONS?.pipedriveMessage?.readAll?.url;
        if (!getUrl) { setIsFetching(false); return; }
        axios.get(getUrl)
            .then(data => setMessages(
                (data ?? [])
            ))
            .catch(() => setFetchError(true))
            .finally(() => setIsFetching(false));
    }, []);

    const typeOptions = [...new Set(messages.map(m => m.type).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
    const statusOptions = Object.keys(STATUS_VARIANTS);

    const areFiltersApplied = !!(filterType || filterStatus || showIgnored || showTraite || search);

    const setParamsToUrl = (newParams) => {
        const paramsToSet = {};
        for (const [key, value] of searchParam.entries()) {
            paramsToSet[key] = value;
        }
        for (const [key, value] of newParams) {
            paramsToSet[key] = value;
        }
        setSearchParam(paramsToSet);
    };

    const onFilterTypeChange = e => {
        setFilterType(e.target.value);
        setParamsToUrl([['type', e.target.value]]);
    };

    const onFilterStatusChange = e => {
        setFilterStatus(e.target.value);
        setParamsToUrl([['status', e.target.value]]);
    };

    const onShowIgnoredChange = e => {
        setShowIgnored(e.target.checked);
        setParamsToUrl([['showIgnored', e.target.checked]]);
    };

    const onShowTraiteChange = e => {
        setShowTraite(e.target.checked);
        setParamsToUrl([['showTraite', e.target.checked]]);
    };

    const resetFilters = () => {
        setFilterType('');
        setFilterStatus('');
        setShowIgnored(false);
        setShowTraite(false);
        resetSearchBar();
        setParamsToUrl([['type', ''], ['status', ''], ['showIgnored', false], ['showTraite', false]]);
    };

    const isIgnoredVisible = showIgnored || filterStatus === 'Ignoré';
    const isTraiteVisible = showTraite || filterStatus === 'Traité';

    const filteredMessages = messages.filter(m =>
        (!filterType || m.type === filterType) &&
        (!filterStatus || m.status === filterStatus) &&
        (isIgnoredVisible || m.status !== 'Ignoré') &&
        (isTraiteVisible || m.status !== 'Traité') &&
        (HelperMetiers360.isSearchInText(search, m.title)
            || HelperMetiers360.isSearchInText(search, m.clientName)
            || HelperMetiers360.isSearchInText(search, m.note))
    );

    const handleClick = (uniqueId) => {
        if (pipedriveSlugs?.readOne)
            navigate(pipedriveSlugs.readOne.replace(':uniqueId', uniqueId), { state: { olderSearchParams: location.search } });
    };

    const contentSort = [
        {
            value: 'title',
            label: 'Titre',
            test: true,
            method: (a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'fr'),
            display: m => m.title,
            flatDisplay: m => m.title ?? '',
        },
        {
            value: 'clientName',
            label: 'Client',
            test: true,
            method: (a, b) => (a.clientName ?? '').localeCompare(b.clientName ?? '', 'fr'),
            display: m => m.clientName,
            flatDisplay: m => m.clientName ?? '',
        },
        {
            value: 'date',
            label: 'Date',
            test: true,
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(toDate(a.date), toDate(b.date)),
            display: m => formatDate(m.date),
            flatDisplay: m => formatDate(m.date),
        },
        {
            value: 'type',
            label: 'Type',
            test: true,
            method: (a, b) => (a.type ?? '').localeCompare(b.type ?? '', 'fr'),
            display: m => m.type,
            flatDisplay: m => m.type ?? '',
        },
        {
            value: 'clientUniqueId',
            label: 'Lien Backoffice',
            test: true,
            method: (a, b) => (a.clientUniqueId ?? '').localeCompare(b.clientUniqueId ?? '', 'fr'),
            display: m => m.clientUniqueId ? 'Oui' : 'Non',
            flatDisplay: m => m.clientUniqueId ? 'Oui' : 'Non',
        },
        {
            value: 'axonautId',
            label: 'Lien Axonaut',
            test: true,
            method: (a, b) => (a.axonautId ? 1 : 0) - (b.axonautId ? 1 : 0),
            display: m => m.axonautId ? 'Oui' : 'Non',
            flatDisplay: m => m.axonautId ? 'Oui' : 'Non',
        },
        {
            value: 'status',
            label: 'Statut',
            test: true,
            method: (a, b) => (a.status ?? '').localeCompare(b.status ?? '', 'fr'),
            display: m => <Badge bg={STATUS_VARIANTS[m.status] ?? 'secondary'}>{m.status}</Badge>,
            flatDisplay: m => m.status ?? '',
        },
        {
            value: 'note',
            label: 'Note',
            test: true,
            method: (a, b) => (a.note ?? '').localeCompare(b.note ?? '', 'fr'),
            display: m => m.note
                ? <>
                    Oui&nbsp;
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{m.note}</Tooltip>}
                    >
                        <i className="fas fa-sticky-note text-muted" style={{ cursor: 'pointer' }} />
                    </OverlayTrigger>
                </>
                : 'Non',
            flatDisplay: m => m.note ? 'Oui' : 'Non',
        },
    ];

    return (
        <Container fluid>
            <Row>
                <Col><h2>Actions Pipedrive</h2></Col>
            </Row>
            {!isFetching && messages.length > 0 && (
                <>
                    <Row className={"d-flex align-items-center pe-0"}>
                        <Col className="d-flex align-items-center">
                            <Button id="btnFilters" className="cursor-pointer shadow"
                                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                                onKeyDown={() => setIsFiltersOpen(!isFiltersOpen)}>
                                <p className="me-3">Filtres</p>
                                { isFiltersOpen
                                    ? <i className="fa fa-caret-up"></i>
                                    : <i className="fa fa-caret-down"></i>
                                }
                            </Button>
                            { areFiltersApplied &&
                                <OverlayTrigger placement="top"
                                    overlay={ <Tooltip>Effacer les filtres</Tooltip> }>
                                    <Button id="btnInitFilters" className="shadow" variant="danger" size="sm" onClick={resetFilters}>
                                        <i className="fas fa-redo"></i>
                                    </Button>
                                </OverlayTrigger>
                            }
                        </Col>
                        <Col className="pe-0">
                            {searchInput}
                        </Col>
                    </Row>
                    <div id="filtersZone" className={!isFiltersOpen ? "hide-filters" : undefined}>
                        <Row className="d-flex align-items-center justify-content-between">
                            <Col className="m360-col3-start" md={6} lg={6} xxl={4}>
                                <div className="d-flex align-items-center">
                                    <label className="label-select" htmlFor="type-select">Type&nbsp;: </label>
                                    <Form.Select value={filterType} onChange={onFilterTypeChange} id="type-select" className="w-auto">
                                        <option value="">Tous les types</option>
                                        {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                    </Form.Select>
                                </div>
                            </Col>
                            <Col className="m360-col3-center" md={6} lg={6} xxl={4}>
                                <div className="d-flex align-items-center">
                                    <label className="label-select" htmlFor="status-select">Statut&nbsp;: </label>
                                    <Form.Select value={filterStatus} onChange={onFilterStatusChange} id="status-select" className="w-auto">
                                        <option value="">Tous les statuts</option>
                                        {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                    </Form.Select>
                                </div>
                            </Col>
                        </Row>
                        <Row className="d-flex align-items-center justify-content-between mt-2">
                            <Col className="m360-col3-start" md={6} lg={6} xxl={4}>
                                <Form.Check id="show-ignored" className="clickable" label="Afficher les messages ignorés"
                                    checked={showIgnored} onChange={onShowIgnoredChange} />
                            </Col>
                            <Col className="m360-col3-center" md={6} lg={6} xxl={4}>
                                <Form.Check id="show-traite" className="clickable" label="Afficher les messages traités"
                                    checked={showTraite} onChange={onShowTraiteChange} />
                            </Col>
                        </Row>
                    </div>
                </>
            )}
            {isFetching && <Loader />}
            {!isFetching && fetchError && (
                <Alert variant="danger" className="mt-3">
                    Impossible de récupérer les actions Pipedrive. Veuillez réessayer plus tard.
                </Alert>
            )}
            {!isFetching && !fetchError && messages.length === 0 && (
                <Alert variant="info" className="mt-3">
                    Aucune action Pipedrive à afficher.
                </Alert>
            )}
            {!isFetching && messages.length > 0 && (
                <Row>
                    <Col>
                        <DynamicTable
                            contentTable={filteredMessages}
                            contentSort={contentSort}
                            index="uniqueId"
                            tableName="pipedrive-messages"
                            valueInitSort="date"
                            handleClick={handleClick}
                            withParams={true}
                        />
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default PipedrivePage;
